import type {
  AudienceSelectionRef,
  QuestionnaireReviewOptions,
} from "@digital-twins/shared-types";
import { updateQuestionnaireDraftApi } from "@/features/studies/questionnaire/api/questionnaireDraftsApi";
import { isQuestionnaireDraftConflict } from "@/features/studies/questionnaire/utils/compileErrorMessage";
import type { ReviewPin } from "../state/creationState";

/**
 * What one Review write did. `saved` moved the draft from the reviewed
 * revision `from` to `revision`, whose audience is `audience`: Review re-pins
 * to it, since the write changed nothing else. `conflict` means the draft
 * moved past the reviewed revision (another tab's save): the pin stays, and
 * launch refuses it as `questionnaire_launch_state_changed`.
 */
export type ReviewDraftWrite =
  | {
      kind: "saved";
      from: number;
      revision: number;
      audience: AudienceSelectionRef;
    }
  | { kind: "unchanged" }
  | { kind: "superseded" }
  | { kind: "no_audience" }
  | { kind: "unpinned" }
  | { kind: "conflict" };

/** The Review settings written to the draft, one newest value each. */
type ReviewWriteField = "sampleSize" | "reviewOptions";

/**
 * One in-flight Review write per draft. Every Review write is a
 * compare-and-swap on the same pin, so they run one at a time on one chain,
 * each reading the pin when it runs: two chains would refuse each other.
 * Writes overlap (a debounce that has already started cannot be cancelled,
 * and calls finish in either order), so only the newest requested value of
 * each field is written; an older one still queued is `superseded`.
 */
interface ReviewWriteLane {
  latest: Partial<Record<ReviewWriteField, unknown>>;
  tail: Promise<void>;
}

const reviewWriteLanes = new Map<string, ReviewWriteLane>();

/** Drops queued writes. Tests call this so one case cannot block the next. */
export function resetReviewDraftWritesForTests(): void {
  reviewWriteLanes.clear();
}

/**
 * Queue `write` on the draft's lane. It runs only if `value` is still the
 * newest requested value of `field` when its turn comes.
 */
export function queueReviewDraftWrite(
  draftId: string,
  field: ReviewWriteField,
  value: unknown,
  write: () => Promise<ReviewDraftWrite>,
): Promise<ReviewDraftWrite> {
  const lane = reviewWriteLanes.get(draftId) ?? {
    latest: {},
    tail: Promise.resolve(),
  };
  lane.latest[field] = value;
  reviewWriteLanes.set(draftId, lane);

  const run = lane.tail.then(() =>
    lane.latest[field] === value ? write() : { kind: "superseded" as const },
  );
  lane.tail = run.then(
    () => undefined,
    () => undefined,
  );
  return run;
}

/**
 * The write itself: a compare-and-swap on the reviewed revision. It never
 * reads the latest draft to write over: a question saved in another tab after
 * Review must stay unreviewed, and launch must refuse it. A write that lands
 * moves `pin.current` before it resolves, so a write queued behind it, and a
 * launch waiting on it, use the revision it made.
 */
export async function writeAtReviewPin(
  draftId: string,
  pin: { current: ReviewPin },
  revision: number,
  body: Record<string, unknown>,
): Promise<ReviewDraftWrite> {
  try {
    const written = await updateQuestionnaireDraftApi(draftId, {
      ...body,
      expectedRevision: revision,
    });
    const saved = {
      kind: "saved" as const,
      from: revision,
      revision: written.revision,
      audience: written.artifacts.draft.audience,
    };
    pin.current = { revision: saved.revision, audience: saved.audience };
    return saved;
  } catch (error) {
    if (isQuestionnaireDraftConflict(error)) {
      return { kind: "conflict" };
    }
    throw error;
  }
}

/**
 * Store Review's launch options (quick study, deck, twin chat) on the draft,
 * as `draft.reviewOptions`, so a reload restores them. Written when the user
 * changes one on Review.
 */
export function persistReviewOptions(
  draftId: string,
  reviewOptions: QuestionnaireReviewOptions,
  pin: { current: ReviewPin },
): Promise<ReviewDraftWrite> {
  return queueReviewDraftWrite(draftId, "reviewOptions", reviewOptions, () => {
    const revision = pin.current.revision;
    if (revision === null) {
      return Promise.resolve({ kind: "unpinned" as const });
    }
    return writeAtReviewPin(draftId, pin, revision, { reviewOptions });
  });
}
